import { addDays, endOfDayIso, isoToWallClock, isCalendarDay } from './wall-clock';
/**
 * ETKİNLİK KREDİSİ — PAKETLERİN VE TAVAN KADEMELERİNİN TEK KAYNAĞI
 * (kurum satışla açılır planı · Story D1 · E1, kullanıcı kararları K4, K6, K7).
 *
 * Etkinlik yayınlamak kredi ister. Kredi yalnız panelden yüklenir; webde ve
 * mobilde ödeme YOKTUR. Miktar, süre ve etkinlik başına katılımcı tavanı
 * YALNIZCA bu dosyada yazılıdır: panel yüklemesi, landing, paket ekranı ve API
 * tavan kapısı buradan okur (`credit-single-source.guard.spec.ts` sayıların
 * başka yerde yazılmasını yakalar).
 *
 * Defter (`organization_credit_entries`) append-only'dir; bakiye defterden
 * HESAPLANIR, tutulmaz (`computeCreditBalance`).
 */
/** Hakkın (yüklemenin) türü. Kurumsal sözleşme bir hak değil, ayrı tablodur. */
export const CREDIT_GRANT_KINDS = ['trial', 'single', 'pack_12'];
/**
 * Defter hareketinin türü. `grant` hakkın yüklendiği anı defterde iz olarak
 * bırakır (tutarı hakkın miktarına EŞİTTİR, veritabanı zorlar); bakiye hesabı
 * hakkın miktarını ve diğer hareketleri toplar, `grant` satırını iki kez saymaz.
 */
export const CREDIT_ENTRY_KINDS = [
    'grant',
    'consume',
    'consume_contract',
    'refund',
    'expire',
    'reversal',
];
/** Defter satırının aktörü. Kişisel veri yok, yalnız kimlik (5.1 KVKK notu). */
export const CREDIT_ACTOR_TYPES = ['staff', 'user', 'system'];
/** Satılan paketler. `enterprise` Kurumsal sözleşmedir (kredi sayılmaz). */
export const CREDIT_PACKAGES = ['trial', 'single', 'pack_12', 'enterprise'];
/**
 * Etkinliğin katılımcı tavanı kademesi (yol B, bölüm 2): tavan kurum
 * paketinden değil, etkinliğin hangi krediyle açıldığından türer.
 */
export const EVENT_CEILING_TIERS = ['trial', 'standard', 'unlimited'];
/**
 * Tavana ulaşıldığında katılım ne olur (V18-e). `hard`: yeni kayıt, davet
 * kabulü ve başvuru onayı reddedilir. `soft`: reddedilmez, yalnız organizatör
 * araçları kilitlenir (Faz 4'ün eski P1 davranışı). Bugün tüm kademeler sert.
 */
export const CEILING_BEHAVIORS = ['hard', 'soft'];
/** "Bu etkinlik nasıl ödendi" (E1 · okuma ucu). `none` = defterde harcama yok. */
export const EVENT_FUNDING_SOURCES = ['trial', 'single', 'pack_12', 'contract', 'none'];
/** Süre hesabının saat dilimi (V7): 12'li paket ve sözleşme o günün SONUNA kadar geçerli. */
export const CREDIT_TIMEZONE = 'Europe/Istanbul';
/** Kredi defteri metin sütunlarının uzunluk tavanı (`CreditLedger` migration). */
export const CREDIT_TEXT_LIMITS = { reference: 120, reason: 500 };
/**
 * FİYAT BU TABLODA YOKTUR (para kırmızı çizgisi, 30.10.2026). Bu paket her
 * istemcinin (web, mobil, panel) JS paketine girer; fiyat yalnız sunucudadır
 * (`apps/api/src/modules/credits/credit-price-list.ts`) ve yalnız yetkili uçtan
 * gelir. Buraya fiyat alanı eklenirse `credit-price-client-leak.guard.spec` kırılır.
 */
export const CREDIT_PACKAGE_DEFINITIONS = {
    trial: { credits: 1, validity: 'perpetual', ceilingTier: 'trial' },
    single: { credits: 1, validity: 'perpetual', ceilingTier: 'standard' },
    pack_12: { credits: 12, validity: 'oneYearFromPurchase', ceilingTier: 'standard' },
    enterprise: { credits: null, validity: 'contract', ceilingTier: 'unlimited' },
};
export const EVENT_CEILING_TIER_DEFINITIONS = {
    trial: { attendeeCeiling: 100, behavior: 'hard', invitationCeiling: 150, teamMemberCeiling: 2 },
    standard: {
        attendeeCeiling: 1000,
        behavior: 'hard',
        invitationCeiling: 2000,
        teamMemberCeiling: 10,
    },
    unlimited: {
        attendeeCeiling: null,
        behavior: 'hard',
        invitationCeiling: null,
        teamMemberCeiling: null,
    },
};
/** Hakkın türünden açtığı etkinliğin tavan kademesine. */
export function ceilingTierForGrantKind(kind) {
    return CREDIT_PACKAGE_DEFINITIONS[kind].ceilingTier;
}
/**
 * 12'li paketin bitişi (V7 · K7): SATIN ALMA gününün (Europe/Istanbul) bir yıl
 * sonrasının GÜN SONU. Yükleme tarihi değil satın alma tarihi esastır. 29 Şubat
 * ertesi yılda 28 Şubat'a düşer (veritabanındaki `date + interval '1 year'`
 * ile aynı kural; migration CHECK'i bu eşitliği zorlar).
 */
export function pack12ExpiresAt(purchasedAt) {
    const purchaseDay = isoToWallClock(purchasedAt.toISOString(), CREDIT_TIMEZONE).slice(0, 10);
    const [year, month, day] = purchaseDay.split('-');
    const nextYear = String(Number(year) + 1).padStart(4, '0');
    let target = `${nextYear}-${month}-${day}`;
    if (!isCalendarDay(target))
        target = addDays(`${nextYear}-${month}-01`, 27);
    return new Date(endOfDayIso(target, CREDIT_TIMEZONE));
}
/** Takvim gününün (Europe/Istanbul) gün sonu: sözleşme bitişi bu anı taşır. */
export function creditDayEnd(day) {
    const iso = endOfDayIso(day, CREDIT_TIMEZONE);
    if (iso === '')
        throw new Error(`invalid calendar day: ${day}`);
    return new Date(iso);
}
/** Hak bu anda geçerli mi: süresiz ya da bitişin (dahil) önü. */
export function isGrantActive(grant, now) {
    return grant.expiresAt === null || now.getTime() <= grant.expiresAt.getTime();
}
/** Sözleşme bu anda etkin mi: başlangıç ≤ an ≤ bitiş ve erken sonlandırılmamış. */
export function isContractActive(contract, now) {
    const at = now.getTime();
    if (at < contract.startsAt.getTime() || at > contract.endsAt.getTime())
        return false;
    return contract.terminatedAt === null || at < contract.terminatedAt.getTime();
}
/** Hakkın kalan kredisi = miktar + o hakka ait (`grant` dışı) hareketlerin toplamı. */
export function grantRemaining(grant, entries) {
    return entries
        .filter((entry) => entry.grantId === grant.id && entry.kind !== 'grant')
        .reduce((sum, entry) => sum + entry.amount, grant.amount);
}
/** Kurumun bakiyesi — saf; sunucu, panel ve testler aynı sonucu üretir. */
export function computeCreditBalance(input) {
    const byKind = { trial: 0, single: 0, pack_12: 0 };
    for (const grant of input.grants) {
        if (!isGrantActive(grant, input.now))
            continue;
        byKind[grant.kind] += Math.max(grantRemaining(grant, input.entries), 0);
    }
    return {
        available: byKind.trial + byKind.single + byKind.pack_12,
        byKind,
        contractActive: input.contracts.some((contract) => isContractActive(contract, input.now)),
    };
}
const SPEND_ORDER = { pack_12: 0, single: 1, trial: 2 };
/**
 * HARCAMA SIRASI (V4): en erken yanacak 12'li → süresiz tekli → deneme.
 * Aynı türde en erken bitiş, sonra en eski yükleme önce harcanır. Sözleşme
 * kontrolü çağıranın işidir (etkin sözleşme varsa kredi hiç harcanmaz).
 */
export function pickGrantForConsumption(grants, entries, now) {
    const candidates = grants
        .filter((grant) => isGrantActive(grant, now) && grantRemaining(grant, entries) > 0)
        .sort((a, b) => {
        const order = SPEND_ORDER[a.kind] - SPEND_ORDER[b.kind];
        if (order !== 0)
            return order;
        const expiry = (a.expiresAt?.getTime() ?? Number.MAX_SAFE_INTEGER) -
            (b.expiresAt?.getTime() ?? Number.MAX_SAFE_INTEGER);
        if (expiry !== 0)
            return expiry;
        return a.grantedAt.getTime() - b.grantedAt.getTime();
    });
    return candidates[0] ?? null;
}
/**
 * Etkinliğin tavan kademesi (V19): kurumun ŞU AN etkin sözleşmesi varsa
 * sınırsız · sözleşmeyle yayınlandıysa sınırsız (sözleşme bitse de) · kredi
 * türü belirler · defterde harcama yoksa en dar kademe.
 */
export function resolveEventCeilingTier(input) {
    if (input.organizationContractActive)
        return 'unlimited';
    switch (input.fundingSource) {
        case 'contract':
            return 'unlimited';
        case 'single':
        case 'pack_12':
            return 'standard';
        case 'trial':
        case 'none':
            return 'trial';
    }
}
//# sourceMappingURL=credit.js.map